import React, { useState, useEffect } from "react";
import axios from "axios";
import { useUser } from "../../context/userContext";
import { Link } from "react-router-dom";
import TopUpDialog from "./topUpDialog.component";
import Tooltip from "../Common/tooltip.component";
import { ReactComponent as RocketIcon } from "../../assets/icons/rocket_icon.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info_icon.svg";
import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron_icon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_icon.svg";
import { ReactComponent as UpgradePlanIcon } from "../../assets/icons/plan_expired_icon.svg";
import { ReactComponent as UpgradeIcon } from "../../assets/icons/upgrade_icon.svg";
import { ReactComponent as PaymentSuccessIcon } from "../../assets/icons/payment_success_icon.svg";
import DashboardMeterSVG from "./dashboardMeter.component";

function Dashboard() {
  const { userData, fetchDashboardData, scrollHeight } = useUser();
  const {
    session_stats,
    token_stats,
    subscription_plan,
    subscription_frequency,
    valid_through,
  } = userData;

  const [isFreeTrialDialogOpen, setIsFreeTrialDialogOpen] = useState(false);
  const [isPaymentSuccessDialogOpen, setIsPaymentSuccessDialogOpen] =
    useState(false);

  let freeTrialDialogText;
  switch (subscription_plan) {
    case "Free Tier":
      freeTrialDialogText = "Free Trial Expired!";
      break;
    default:
      freeTrialDialogText = "Plan Validity Expired!";
      break;
  }

  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const toggleDashboard = () => {
    setIsDashboardOpen((prevIsDashboardOpen) => !prevIsDashboardOpen);
    setIsTokenPromptOpen(true);
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const [isTokenPromptOpen, setIsTokenPromptOpen] = useState(false);

  const [isFront, setIsFront] = useState(
    token_stats?.daily_subscription_tokens_remaining > 0 ? false : true
  );
  const handleCardClick = () => {
    if (token_stats?.daily_subscription_tokens_remaining > 0) {
      setIsFront(!isFront);
      setIsTokenPromptOpen(false);
    }
  };

  const progressSession =
    (session_stats?.remaining / session_stats?.total) * 100;
  const progressToken =
    (token_stats?.remaining /
      token_stats?.total) *
    100;

  const dailyProgressToken =
    (token_stats?.daily_subscription_tokens_remaining /
      token_stats?.daily_subscription_tokens_total) *
    100;

  useEffect(() => {
    fetchDashboardData(() => setIsFreeTrialDialogOpen(true));

    const intervalId = setInterval(() => {
      fetchDashboardData(() => setIsFreeTrialDialogOpen(true));
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      {/* Session Limit Reached Dialog */}
      {isFreeTrialDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.24)] z-50">
          <div className="fixed inset-0 bg-black opacity-50" />

          <div className="relative flex flex-col justify-center items-center p-4 w-[90%] md:w-2/3 lg:w-3/4 xl:w-1/3 bg-white rounded-xl">
            <div className="absolute top-3 right-3 flex rounded-full cursor-pointer hover:scale-[105%]">
              <CloseIcon
                className="w-8 h-8"
                onClick={() => setIsFreeTrialDialogOpen(false)}
              />
            </div>
            <UpgradePlanIcon className="w-48 h-48" />
            <div className="flex flex-col justify-center items-center mb-8">
              <h2 className="mt-4 text-gray-900 text-2xl font-semibold">
                {freeTrialDialogText}
              </h2>
              <p className="mt-1 mx-2 text-center text-sm md:text-base text-gray-400 font-medium">
                Please upgrade your plan to create <br /> new sessions.
              </p>
            </div>
            <div className="flex justify-center items-center w-4/5 mb-4 bg-primary/10 rounded">
              <button className="flex justify-center items-center w-4/5 bg-primary text-white m-4 px-2 py-2 rounded">
                <UpgradeIcon className="w-4 h-4 mr-2" fill="white" />
                <Link to="/#pricing">Upgrade Now</Link>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success Dialog */}
      {isPaymentSuccessDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[100]">
          <div className="fixed inset-0 bg-black/40" />

          <div className="relative flex flex-col justify-center items-center p-4 w-[90%] md:w-2/3 lg:w-3/4 xl:w-1/3 bg-white rounded-xl">
            <div className="absolute top-3 right-3 flex rounded-full cursor-pointer hover:scale-[105%]">
              <CloseIcon
                className="w-8 h-8"
                onClick={() => setIsPaymentSuccessDialogOpen(false)}
              />
            </div>
            <PaymentSuccessIcon className="mt-4 w-48 h-48" />
            <div className="flex flex-col justify-center items-center mb-8">
              <h2 className="mt-4 text-gray-900 text-2xl font-semibold">
                Payment Successful
              </h2>
              <p className="mt-2 mx-2 text-center text-sm md:text-base text-gray-400 font-medium">
                Your payment was successful. You can now continue{" "}
                <br className="hidden md:block" /> using XplorEase.
              </p>
            </div>
            <div className="flex justify-center items-center w-4/5 mb-4 bg-primary/10 rounded">
              <button
                className="flex justify-center items-center w-4/5 bg-primary text-white m-4 px-2 py-2 rounded"
                onClick={() => setIsPaymentSuccessDialogOpen(false)}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Component */}
      {isDialogOpen && (
        <TopUpDialog
          onClose={handleCloseDialog}
          setIsPaymentSuccessDialogOpen={setIsPaymentSuccessDialogOpen}
        />
      )}

      <div className="container mx-auto p-4 bg-white/50 border border-neutral-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg sm:text-xl lg:text-2xl text-neutral-900 font-semibold">
              Account Details{" "}
              {/* <span className="text-lg sm:text-xl lg:text-2xl text-gray-400">
                / {subscription_plan}
              </span> */}
            </h2>
            <p
              className={`${
                isDashboardOpen ? "hidden" : ""
              } mt-1 text-gray-400 text-sm sm:text-base sm:font-medium`}
            >
              Your subscription plan details appear here.
            </p>
          </div>
          <button
            onClick={toggleDashboard}
            className="bg-gray-200 md:mr-2 p-1 rounded-xl shadow"
          >
            <ChevronIcon
              className={`w-8 md:w-10 h-8 md:h-10 transform transition-transform duration-300 ${
                isDashboardOpen ? "rotate-0" : "rotate-180"
              }`}
            />
          </button>
        </div>

        <div
          className={`transition-opacity ease-out duration-300 opacity-${
            isDashboardOpen ? "100" : "0"
          }`}
        >
          {isDashboardOpen && (
            <>
              <div className="mt-2 mb-4 border-b border-neutral-200" />

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 xl:gap-2 2xl:gap-4">
                <div className="flex flex-col justify-center">
                  {/* <h3 className="sm:text-lg font-medium text-gray-400 mb-2 md:mb-3">
                    Plan Details:
                  </h3> */}
                  <p className="md:font-medium md:text-lg text-gray-400 ml-1 2xl:ml-2 mb-1 md:mb-2">
                    Subscription Plan -{" "}
                    <span className="text-gray-600">{subscription_plan}</span>
                  </p>
                  <p className="md:font-medium md:text-lg text-gray-400 ml-1 2xl:ml-2 mb-1 md:mb-2">
                    Subscription Frequency -{" "}
                    <span className="text-gray-600">
                      {subscription_frequency}
                    </span>
                  </p>
                  <p className="md:font-medium md:text-lg text-gray-400 ml-1 2xl:ml-2 mb-1 md:mb-2">
                    Valid Through -{" "}
                    <span className="text-gray-600">
                      {valid_through?.split(" ").slice(0, 4).join(" ")}
                    </span>
                  </p>
                </div>

                <div className="hidden md:flex xl:hidden flex-col justify-center p-4">
                  <div className="flex mb-2">
                    <p className="w-3/5 pr-2 text-sm text-gray-500 flex">
                      <span className="mt-[3px] mr-1">
                        <InfoIcon
                          className="w-4 h-4 cursor-pointer"
                          fill="none"
                          stroke="#6495ED"
                        />
                      </span>
                      Top-up to add more tokens.
                    </p>
                    <button
                      className="w-2/5 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-800 focus:outline-none focus:shadow-outline-blue active:bg-blue-800"
                      onClick={handleOpenDialog}
                    >
                      Top-Up
                    </button>
                  </div>
                  <div className="border-b border-neutral-200" />
                  <div className="flex items-center mt-2">
                    <p className="w-3/5 pr-2 text-sm text-gray-500 flex">
                      <span className="mt-[3px] mr-1">
                        <InfoIcon
                          className="w-4 h-4 cursor-pointer"
                          fill="none"
                          stroke="#6495ED"
                        />
                      </span>
                      Upgrade your plan !
                    </p>
                    <button className="w-2/5 bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-800 focus:outline-none focus:shadow-outline-blue active:bg-blue-800">
                      <Link to="/#pricing">Subscribe</Link>
                    </button>
                  </div>
                </div>

                {/* Session Stats */}
                <div className="flex items-center p-2 md:p-4 bg-white rounded-lg shadow-session">
                  <div className="flex flex-col justify-center items-start w-3/5 md:w-1/2">
                    <div className="ml-4">
                      <h3 className="text-lg md:font-medium text-gray-400 mb-[6px] md:mb-3 leading-snug md:leading-normal">
                        Sessions Available
                      </h3>
                      <p className="text-xl md:text-2xl font-medium text-gray-600 mb-2">
                        {session_stats?.remaining}/{session_stats?.total}
                      </p>
                    </div>
                  </div>
                  <div className="relative flex justify-between w-2/5 md:w-1/2">
                    {window.innerWidth < 768 ? (
                      <DashboardMeterSVG
                        width={100}
                        height={100}
                        radius={45}
                        strokeWidth={7}
                        fontSize={12}
                        progress={progressSession}
                      />
                    ) : (
                      <DashboardMeterSVG
                        className="hidden md:block"
                        width={120}
                        height={120}
                        radius={55}
                        strokeWidth={8}
                        fontSize={16}
                        progress={progressSession}
                      />
                    )}
                    <Tooltip
                      className="bottom-[105%] left-[-350%] md:left-[52%]"
                      width="280px"
                      content="A session is a knowledge base created by uploading a set of documents having content with similar topics that is accessible to the user by scanning a QR code."
                    >
                      <InfoIcon
                        className="mt-[2px] mr-[2px] md:mt-0 md:mr-0 w-7 h-7 p-[2px] border border-neutral-400 rounded-md bg-gray-50 cursor-pointer"
                        fill="none"
                        stroke="#6495ED"
                      />
                    </Tooltip>
                  </div>
                </div>

                {/* Token Stats */}

                {isDashboardOpen &&
                  token_stats?.daily_subscription_tokens_remaining > 0 &&
                  isTokenPromptOpen && (
                    <div
                      className="absolute top-0 left-0 w-full bg-gray-800 bg-opacity-50 z-50"
                      style={{ height: scrollHeight }}
                    >
                      <div className="absolute top-[5%] left-[5%] md:top-[6%] md:left-[4%] lg:top-[10%] lg:left-[4%] xl:top-[5%] xl:left-[5%] flex items-center p-8 bg-white rounded-3xl">
                        <div>
                          <p className="mb-1 md:mb-2 text-sm font-semibold text-blue-600">
                            Hi {userData.first_name}!
                          </p>
                          <h2 className="mb-2 md:mb-3 font-semibold text-lg md:text-2xl text-black">
                            Checkout the available <br /> Daily Tokens.
                          </h2>
                          <p className="text-sm">
                            Click on the{" "}
                            <span className="font-semibold">"Tokens Tab"</span>{" "}
                            to see <br />
                            the available daily tokens.
                          </p>
                          <div className="flex mt-4">
                            <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                            <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                            <div className="w-2 h-2 mr-1 bg-blue-600 rounded-full"></div>
                          </div>
                        </div>
                        <div>
                          <RocketIcon className="hidden md:block w-36 h-36 p-2 ml-6 border rounded-full" />
                        </div>
                      </div>
                    </div>
                  )}

                <div
                  className={`${
                    token_stats?.daily_subscription_tokens_remaining > 0 &&
                    "pulse-button"
                  } ${token_stats?.daily_subscription_tokens_remaining > 0 && isTokenPromptOpen ? "z-[100]" : ""} rounded-lg`}
                >
                  <div
                    className={`flex items-center p-2 md:p-4 bg-white rounded-lg shadow-session ${
                      token_stats?.daily_subscription_tokens_remaining > 0
                        ? "cursor-pointer md:hover:shadow-card"
                        : ""
                    } transition-all duration-500 [transform-style:preserve-3d] ${
                      isFront ? "" : "[transform:rotateY(180deg)]"
                    }`}
                    onClick={handleCardClick}
                  >
                    {isFront ? (
                      <div className="flex w-full [backface-visibility:hidden]">
                        <div className="flex flex-col justify-center items-start w-3/5 md:w-1/2">
                          <div className="ml-4">
                            <h3 className="text-lg md:font-medium text-gray-400 mt-2 md:mt-0 mb-[6px] md:mb-3 leading-snug md:leading-normal">
                              Plan Tokens Available
                            </h3>
                            <p className="text-xl md:text-2xl font-medium text-gray-600 mb-2">
                              {token_stats?.remaining}
                            </p>
                          </div>
                        </div>
                        <div className="relative flex justify-between w-2/5 md:w-1/2">
                          {window.innerWidth < 768 ? (
                            <DashboardMeterSVG
                              width={100}
                              height={100}
                              radius={45}
                              strokeWidth={7}
                              fontSize={12}
                              progress={progressToken}
                            />
                          ) : (
                            <DashboardMeterSVG
                              width={120}
                              height={120}
                              radius={55}
                              strokeWidth={8}
                              fontSize={16}
                              progress={progressToken}
                            />
                          )}
                          <Tooltip
                            className="bottom-[105%] left-[-350%] md:left-[52%]"
                            width="280px"
                            content="Tokens are credits allocated to users based on their chosen subscription. These tokens are used up as clients ask queries through the XplorEase chat page or over the subscription period, whichever ends first."
                          >
                            <InfoIcon
                              className="mt-[2px] mr-[2px] md:mt-0 md:mr-0 w-7 h-7 p-[2px] border border-neutral-400 rounded-md bg-gray-50 cursor-pointer"
                              fill="none"
                              stroke="#6495ED"
                            />
                          </Tooltip>
                        </div>
                      </div>
                    ) : (
                      <div className="flex w-full [backface-visibility:hidden] [transform:rotateY(180deg)]">
                        <div className="flex flex-col justify-center items-start w-3/5 md:w-1/2">
                          <div className="ml-4">
                            <h3 className="text-lg md:font-medium text-gray-400 mb-[6px] md:mb-3 leading-snug md:leading-normal">
                              Daily Tokens Available
                            </h3>
                            <p className="text-xl md:text-2xl font-medium text-gray-600 mb-2">
                              {token_stats?.daily_subscription_tokens_remaining}
                            </p>
                          </div>
                        </div>
                        <div className="relative flex justify-between w-2/5 md:w-1/2">
                          {window.innerWidth < 768 ? (
                            <DashboardMeterSVG
                              width={100}
                              height={100}
                              radius={45}
                              strokeWidth={7}
                              fontSize={12}
                              progress={dailyProgressToken}
                            />
                          ) : (
                            <DashboardMeterSVG
                              width={120}
                              height={120}
                              radius={55}
                              strokeWidth={8}
                              fontSize={16}
                              progress={dailyProgressToken}
                            />
                          )}
                          <Tooltip
                            className="bottom-[105%] left-[-350%] md:left-[52%]"
                            width="280px"
                            content="Tokens are credits allocated to users based on their chosen subscription. These tokens are used up as clients ask queries through the XplorEase chat page or over the subscription period, whichever ends first."
                          >
                            <InfoIcon
                              className="mt-[2px] mr-[2px] md:mt-0 md:mr-0 w-7 h-7 p-[2px] border border-neutral-400 rounded-md bg-gray-50 cursor-pointer"
                              fill="none"
                              stroke="#6495ED"
                            />
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:hidden xl:flex flex-col justify-center md:p-4">
                  <div className="flex items-center mb-2">
                    <p className="w-3/5 pr-2 text-sm text-gray-500 flex">
                      <span className="mt-[3px] mr-1">
                        <InfoIcon
                          className="w-4 h-4 cursor-pointer"
                          fill="none"
                          stroke="#6495ED"
                        />
                      </span>
                      Top-up to add more tokens.
                    </p>
                    <button
                      className="w-2/5 h-10 bg-primary text-white py-2 px-2 rounded-md hover:bg-blue-800 focus:outline-none focus:shadow-outline-blue active:bg-blue-800"
                      onClick={handleOpenDialog}
                    >
                      Top-Up
                    </button>
                  </div>
                  <div className="border-b border-neutral-200" />
                  <div className="flex items-center mt-2">
                    <p className="w-3/5 pr-2 text-sm text-gray-500 flex">
                      <span className="mt-[3px] mr-1">
                        <InfoIcon
                          className="w-4 h-4 cursor-pointer"
                          fill="none"
                          stroke="#6495ED"
                        />
                      </span>
                      Upgrade your plan !
                    </p>
                    <button className="w-2/5 h-10 bg-primary text-white py-2 px-2 rounded-md hover:bg-blue-800 focus:outline-none focus:shadow-outline-blue active:bg-blue-800">
                      <Link to="/#pricing">Subscribe</Link>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard;
